import type { AssetId, ConceptId, LabelId, ViewId } from "@storytell/types";
import type { Wire } from "~/wire/definition";
import { trackFeature } from "./trackFeature";

const SCOPE_PICKER_DRAG_TYPE = "application/x-scope-picker-item";

/** DOM marker for drags originating from scope-picker rows (excludes from generic in-page file-drop heuristics). */
export const SCOPE_PICKER_DRAGGABLE_SELECTOR = "[data-scope-picker-draggable]";

/** Fallback for browsers that omit custom MIME types during same-document drag. */
const SCOPE_PICKER_TEXT_PLAIN_PREFIX = "x-scope-picker:";

/** True between `setScopePickerDragData` (dragstart) and global `dragend` — used for capture-phase dragover on the prompt bar. */
let scopePickerDragSessionActive = false;

export function isScopePickerDragSessionActive(): boolean {
  return scopePickerDragSessionActive;
}

export function clearScopePickerDragSession(): void {
  scopePickerDragSessionActive = false;
}

export function setScopePickerDragData(dt: DataTransfer, payload: string): void {
  scopePickerDragSessionActive = true;
  dt.setData(SCOPE_PICKER_DRAG_TYPE, payload);
  dt.setData("text/plain", SCOPE_PICKER_TEXT_PLAIN_PREFIX + payload);
}

function getScopePickerPayloadFromDataTransfer(dt: DataTransfer | null | undefined): string | null {
  if (!dt) return null;
  const custom = dt.getData(SCOPE_PICKER_DRAG_TYPE);
  if (custom) return custom;
  const text = dt.getData("text/plain") ?? "";
  return text.startsWith(SCOPE_PICKER_TEXT_PLAIN_PREFIX) ? text.slice(SCOPE_PICKER_TEXT_PLAIN_PREFIX.length) : null;
}

function listDataTransferTypes(dt: DataTransfer | null | undefined): string[] {
  if (!dt?.types) return [];
  const out: string[] = [];
  for (let i = 0; i < dt.types.length; i++) {
    out.push(dt.types[i] ?? "");
  }
  return out;
}

/**
 * True if dragover should call preventDefault so a drop can fire.
 * When only `text/plain` is present (Safari/Firefox same-document drags), require
 * `!dragStartedFromGenericPageElement` so normal text selection drags are unaffected.
 */
export function dataTransferMayCarryScopePickerPayload(
  dt: DataTransfer | null | undefined,
  dragStartedFromGenericPageElement: boolean
): boolean {
  const types = listDataTransferTypes(dt);
  if (types.includes(SCOPE_PICKER_DRAG_TYPE)) return true;
  if (types.includes("text/plain") && !dragStartedFromGenericPageElement) return true;
  return false;
}

/**
 * Applies scope-picker JSON payload to chat scope. Used for drops on the prompt bar / document.
 * addAll for assets uses selectAllAssets() (same as non-search "add all" in the picker).
 */
export function tryHandleScopePickerDrop(e: DragEvent, wire: Wire): boolean {
  const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
  if (files.length > 0) return false;

  const raw = getScopePickerPayloadFromDataTransfer(e.dataTransfer);
  if (!raw) return false;

  const scope = wire.chat.scope;
  try {
    const payload = JSON.parse(raw) as { type?: string; id?: string; addAll?: boolean; entityType?: string };
    if (payload.addAll && payload.entityType) {
      if (payload.entityType === "asset") {
        if (!scope.isUsingAllAssets()) scope.selectAllAssets();
      } else if (payload.entityType === "label") {
        for (const label of wire.labels.all()) {
          if (!scope.labels().includes(label.id)) scope.addLabel(label.id);
        }
      } else if (payload.entityType === "view") {
        for (const view of wire.views.all()) {
          if (!scope.views().includes(view.id)) scope.addView(view.id);
        }
      } else if (payload.entityType === "concept") {
        for (const concept of wire.concepts.all()) {
          if (!scope.concepts().includes(concept.id)) scope.addConcept(concept.id);
        }
      } else {
        return false;
      }
    } else if (payload.type && payload.id) {
      if (payload.type === "asset") {
        const id = payload.id as AssetId;
        if (scope.isUsingAllAssets() && !scope.assets().includes(id)) {
          return true;
        }
        if (scope.assets().includes(id)) scope.removeAsset(id);
        else scope.addAsset(id);
      } else if (payload.type === "label") {
        const id = payload.id as LabelId;
        if (scope.labels().includes(id)) scope.removeLabel(id);
        else scope.addLabel(id);
      } else if (payload.type === "concept") {
        const id = payload.id as ConceptId;
        if (scope.concepts().includes(id)) scope.removeConcept(id);
        else scope.addConcept(id);
      } else if (payload.type === "view") {
        const id = payload.id as ViewId;
        if (scope.views().includes(id)) scope.removeView(id);
        else scope.addView(id);
      } else {
        return false;
      }
    } else {
      return false;
    }
  } catch {
    return false;
  }
  trackFeature({ feature: "prompt.add_to_chat", action: "drag", via: "drag" });
  return true;
}
